import { motion } from 'framer-motion';
import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import type { Image } from '../../services/api';
import { getStockStatusDisplay } from '../../services/api';

import styles from './FullscreenImage.module.css';

/**
 * FullscreenImage Props
 */
type FullscreenImageProps = {
  readonly image: Image;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onNext?: () => void;
  readonly onPrevious?: () => void;
  readonly variant?: 'gallery' | 'shop'; // Display variant (shop shows prices/stock)
};

/**
 * FullscreenImage Component
 *
 * Displays a single image in fullscreen modal/overlay with smooth transitions.
 * Features:
 * - Fullscreen modal with dark backdrop
 * - Smooth entry/exit animations
 * - Navigation controls (next/previous)
 * - Close button and ESC key support
 * - Image metadata display (title, description)
 * - Keyboard navigation (arrow keys, ESC)
 *
 * Usage:
 * <FullscreenImage
 *   image={imageObject}
 *   isOpen={true}
 *   onClose={() => setOpen(false)}
 *   onNext={() => handleNext()}
 *   onPrevious={() => handlePrevious()}
 * />
 * @param root0
 * @param root0.image
 * @param root0.isOpen
 * @param root0.onClose
 * @param root0.onNext
 * @param root0.onPrevious
 * @param root0.variant
 */
const FullscreenImage: React.FC<FullscreenImageProps> = ({
  image,
  isOpen,
  onClose,
  onNext,
  onPrevious,
  variant = 'gallery',
}) => {
  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowRight':
          if (onNext) {
            event.preventDefault();
            onNext();
          }
          break;
        case 'ArrowLeft':
          if (onPrevious) {
            event.preventDefault();
            onPrevious();
          }
          break;
      }
    },
    [isOpen, onClose, onNext, onPrevious]
  );

  /**
   * Set up keyboard event listener
   */
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when fullscreen is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  /**
   * Handle backdrop click to close
   */
  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) return null;

  return createPortal(
    <motion.div
      className={styles.fullscreen}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={handleBackdropClick}
    >
      {/* Backdrop - Click to close */}
      <div className={styles.backdrop} onClick={onClose} />

      {/* Close Button */}
      <button className={styles.closeButton} onClick={onClose} aria-label="Close fullscreen view">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      {/* Content Container */}
      <div className={styles.content}>
        {/* Image */}
        <motion.div
          className={styles.imageContainer}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <img src={image.url} alt={image.title ?? image.alt ?? 'Image'} className={styles.image} />
        </motion.div>

        {/* Image Info Container - Only show if there's content to display */}
        {/* Gallery: Show title/description if available | Shop: Show title/description/shop data */}
        {(image.title ?? image.description ?? variant === 'shop') ? (
          <motion.div
            className={styles.imageInfo}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            {/* Title - Only show if title exists */}
            {image.title ? <h2 className={styles.title}>{image.title}</h2> : null}

            {/* Description - Only show if description exists */}
            {image.description ? <p className={styles.description}>{image.description}</p> : null}

            {/* Shop Data - Only show in shop variant */}
            {variant === 'shop' && (
              <div className={styles.shopInfo}>
                {/* Price - from shopData or legacy price field */}
                {(image.shopData?.price !== undefined || image.price !== undefined) && (
                  <p className={styles.price}>
                    ${(image.shopData?.price ?? image.price ?? 0).toFixed(2)}{' '}
                    {image.shopData?.currency && image.shopData.currency !== 'USD'
                      ? image.shopData.currency
                      : null}
                  </p>
                )}

                {/* Stock Status - Default to 'in_stock' if not specified */}
                {(() => {
                  const status = image.shopData?.stockStatus ?? 'in_stock';
                  const display = getStockStatusDisplay(status);
                  return display ? (
                    <div
                      className={styles.stockStatus}
                      style={{
                        color: display.color,
                      }}
                    >
                      <span className={styles.stockIcon}>{display.icon}</span>
                      <span className={styles.stockText}>{display.text}</span>
                    </div>
                  ) : null;
                })()}

                {/* Purchase Button */}
                {image.shopData?.purchaseUrl ? (
                  <a
                    href={image.shopData.purchaseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.buyButton}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {image.shopData.stockStatus === 'out_of_stock' ? 'View Details' : 'Buy Now'}
                    {image.shopData.price !== undefined && ` - $${image.shopData.price.toFixed(2)}`}
                  </a>
                ) : null}
              </div>
            )}
          </motion.div>
        ) : null}
      </div>

      {/* Navigation Controls */}
      {onPrevious ? (
        <button
          className={`${styles.navButton} ${styles.prevButton}`}
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
          aria-label="Previous image"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      ) : null}

      {onNext ? (
        <button
          className={`${styles.navButton} ${styles.nextButton}`}
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          aria-label="Next image"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      ) : null}

      {/* Keyboard Hint */}
      <div className={styles.keyboardHint}>
        <span>ESC or click background to close</span>
        {(onNext ?? onPrevious) ? <span> • Arrow keys to navigate</span> : null}
      </div>
    </motion.div>,
    document.body
  );
};

export default FullscreenImage;
